import Vue from "vue";
import { MutationTree } from "vuex";
import { SiteMonitorCamerasState, MatrixCamera, MatrixStoredFile, MatrixClipContents } from "./types";
import { axiosInstance } from "@/axios.instance";
import { getDefaultState } from "./state";

export const mutations: MutationTree<SiteMonitorCamerasState> = {
	setDeviceControllerOutputs(state, devices: any) {
		devices.forEach(output => {
			Vue.set(output, "controlRequest", false);
			Vue.set(output, "controlReason", "");
			Vue.set(output, "controlError", "");
			Vue.set(output, "sendingCommand", false);
		});
		state.deviceControllerOutputs = devices;
	},
	setAreaCameras: (state, devices: MatrixCamera[]) => (state.areaCameras = devices),
	setDeviceControllerCameras(state, devices: any) {
		if (devices) {
			const matrixDevices: { [deviceId: string]: number } = {};
			const maxtrixContents = state.matrixContents;

			for (const matrixIndex in maxtrixContents) {
				if (maxtrixContents[matrixIndex].camera !== undefined) {
					const camera = maxtrixContents[matrixIndex].camera;
					if (camera != null) {
						matrixDevices[camera.objectID.toString()] = parseInt(matrixIndex, 10);
					}
				}
			}

			devices.forEach(item => {
				if (item.objectID) {
					const deviceID = item.objectID.toString();

					if (matrixDevices[deviceID] !== undefined) {
						Vue.set(item, "index", matrixDevices[deviceID]);
					} else {
						Vue.set(item, "index", null);
					}
				}
			});
		}

		state.deviceControllerCameras = devices;
	},
	clearDeviceControllerCameras(state) {
		state.deviceControllerCameras = [];
	},
	setDeviceControllerStoredFiles(state, storedFiles: MatrixStoredFile[]) {
		// If a given stored file is already in the media matrix, fetches the cell index
		const getIndexForStoredFile = (fileLocation: string) =>
			Object.values(state.matrixContents).find(c => c?.storedFile?.fileLocation == fileLocation)?.storedFile
				.index;

		// Update the list of stored files, adding the index of the file in the matrix if it's present.
		state.deviceControllerStoredFiles = storedFiles.map(file => ({
			...file,
			index: getIndexForStoredFile(file.fileLocation)
		}));
	},
	clearDeviceControllerStoredFiles(state) {
		state.deviceControllerStoredFiles = [];
	},
	setDeviceControllerClips(state, clips: MatrixClipContents[]) {
		state.deviceControllerClips = clips;
	},
	setLayoutIndex(state, index: number) {
		state.layoutIndex = index;

		const width = state.layouts[index][0];
		const height = state.layouts[index][1];
		const maxIndex = width * height;

		state.deviceControllerCameras.forEach(camera => {
			if (camera.index != null && camera.index > maxIndex) {
				camera.index = null;
			}
		});

		state.deviceControllerClips.forEach(clip => {
			if (clip.index != null && clip.index > maxIndex) {
				clip.index = null;
			}
		});

		state.deviceControllerStoredFiles.forEach(file => {
			if (file.index != null && file.index > maxIndex) {
				file.index = null;
			}
		});

		// Remove any media matrix cells which will be clipped from view on resize
		let deletedCell = false;
		for (const cellIndex of Object.keys(state.matrixContents)) {
			if (parseInt(cellIndex, 10) > maxIndex) {
				Vue.delete(state.matrixContents, cellIndex);
				deletedCell = true;
			}
		}

		// Only update the last changed time if we've removed any cells
		if (deletedCell) {
			state.mediaMatrixLastChanged = new Date().valueOf();
		}
	},
	setAwaitingCamera(state, camera: any) {
		state.awaitingCamera = camera;
	},
	setAwaitingStoredFile(state, storedFile: MatrixStoredFile) {
		state.awaitingStoredFile = storedFile;
	},
	setAwaitingClip(state, clip: any) {
		state.awaitingClip = clip;
	},
	setMatrixContents(state, { index, contents }: any) {
		if (contents.camera) {
			Vue.set(contents.camera, "index", index);
		} else if (contents.clip) {
			Vue.set(contents.clip.clip, "index", index);
		} else if (contents.storedFile) {
			Vue.set(contents.storedFile, "index", index);
		}

		Vue.set(state.matrixContents, index.toString(), contents);
		state.mediaMatrixLastChanged = new Date().valueOf();
	},
	setPushContents(state, { index, newContents }: any) {
		Vue.set(state.matrixPushContents, index.toString(), newContents);
	},
	setMediaMatrixIsNew(state, isNew: boolean) {
		state.mediaMatrixIsNew = isNew;
		state.fetchingMatrix = false;
	},
	setFetchingMatrix(state, fetchingMatrix: boolean) {
		state.fetchingMatrix = fetchingMatrix;
	},
	clearMatrixContents(state) {
		state.matrixContents = {};
		state.matrixPushContents = {};
	},
	highlightCell(state, index: number) {
		state.highlightCellIndex = index;
	},
	unhighlightCell(state) {
		state.highlightCellIndex = null;
	},
	setLinkRecord(state, record: any) {
		state.linkRecord = record;
	},
	setSaveLink(state, save) {
		state.saveLink = save;
	},
	setCameraLink(state, camera: any) {
		// @techdebt avoid api requests in mutations
		if (state.saveLink) {
			if (state.linkRecord.attachedCameras == null) {
				state.linkRecord.attachedCameras = [];
			}

			const existingLinks = state.linkRecord.attachedCameras.filter(
				linkedCamera => linkedCamera.deviceID === camera.deviceId
			);
			if (existingLinks.length === 0) {
				axiosInstance
					.get("/Responses/Link/" + state.linkRecord.objectID + "/" + camera.deviceId)
					.then(response => {
						if (response.data.deviceID) {
							state.linkRecord.attachedCameras.push({
								deviceID: response.data.deviceID,
								title: response.data.title
							});
						}

						state.linkRecord = null;
					});
			} else {
				state.linkRecord = null;
			}
		} else {
			state.linkRecord = null;
		}
	},
	setWaitingForEvent(state, waiting: boolean) {
		state.waitingForEvent = waiting;
	},
	setSearchedCameras(state, payload: any[]) {
		state.searchedCameras = payload.map(camera => {
			return {
				objectID: camera.deviceId,
				itemType: 1,
				title: camera.title,
				distance: camera.distance,
				imperial: camera.imperial,
				areaTitle: camera.areaTitle,
				index: null
			};
		});
	},
	setSearchedOutputs(state, payload: any[]) {
		state.searchedOutputs = payload.map(output => {
			return {
				objectID: output.deviceId,
				itemType: 3,
				title: output.title,
				distance: output.distance,
				imperial: output.imperial,
				areaTitle: output.areaTitle,
				controlRequest: output.controlRequest,
				controlReason: output.controlReason,
				controlError: output.controlError
			};
		});
	},
	updateMediaMatrixContents(state, { contents, index }) {
		const matrixContents = state.matrixContents;

		const cellContents = matrixContents[index.toString()];
		if (cellContents != null) {
			if (cellContents.camera) {
				cellContents.camera.index = null;
			} else if (cellContents.clip) {
				cellContents.clip.clip.index = null;
			} else if (cellContents.storedFile) {
				cellContents.storedFile.index = null;
			}
		}

		const deviceControllerCameras = state.deviceControllerCameras;
		if (deviceControllerCameras != null) {
			deviceControllerCameras.forEach(camera => {
				if (camera.index === index) {
					camera.index = null;
				}
				if (contents.camera && contents.camera.objectID === camera.objectID) {
					camera.index = index;
				}
			});
		}
		const deviceControllerClips = state.deviceControllerClips;
		if (deviceControllerClips != null) {
			deviceControllerClips.forEach(clip => {
				if (clip.index === index) {
					clip.index = null;
				}
				if (contents.clip && contents.clip.clip.FileIdentifier === clip.FileIdentifier) {
					clip.index = index;
				}
			});
		}

		const deviceControllerStoredFiles = state.deviceControllerStoredFiles;
		if (deviceControllerStoredFiles != null) {
			deviceControllerStoredFiles.forEach(file => {
				if (file.index === index) {
					file.index = null;
				}
				if (contents.storedFile && contents.storedFile?.fileLocation === file?.fileLocation) {
					file.index = index;
				}
			});
		}
	},
	setShownAuditClips(state, clips: string[]) {
		state.shownAuditClips = clips;
	},
	setSearchedCameraIndex(state, { cameraId, index }) {
		let searchedCamera = state.searchedCameras.find(cam => cam.objectID == cameraId);
		if (!searchedCamera) {
			return;
		}

		Vue.set(searchedCamera, "index", index);
	},
	setAreaCameraIndex(state, { cameraId, index }) {
		let areaCamera = state.areaCameras.find(cam => cam.objectID == cameraId);
		if (!areaCamera) {
			return;
		}

		Vue.set(areaCamera, "index", index);
	},
	resetState(state) {
		Object.assign(state, getDefaultState());
	}
};
